import type { KeyboardEvent } from 'react';

/** Opción del menú que recibe el foco al abrirlo. */
export type MenuStart = 'first' | 'last';

function menuItems(menu: HTMLElement): HTMLElement[] {
  return Array.from(
    menu.querySelectorAll<HTMLElement>(
      '[role="menuitem"]:not([disabled]), [role="menuitemcheckbox"]:not([disabled])',
    ),
  );
}

/** Lleva el foco a la primera o a la última opción del menú recién abierto. */
export function focusMenuItem(menu: HTMLElement | null, start: MenuStart): void {
  const items = menu === null ? [] : menuItems(menu);
  (start === 'first' ? items[0] : items.at(-1))?.focus();
}

/**
 * Teclado del botón que abre el menú (como el Select de la app): ArrowDown lo abre en la
 * primera opción y ArrowUp en la última. Devuelve dónde abrir, o null si la tecla no abre.
 */
export function menuButtonStart(e: KeyboardEvent<HTMLElement>): MenuStart | null {
  if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
    return null;
  }
  e.preventDefault();
  return e.key === 'ArrowDown' ? 'first' : 'last';
}

/**
 * Teclado de un menú desplegable (patrón de menú de WAI-ARIA), para el `onKeyDown` del
 * contenedor `role="menu"`: las flechas recorren las opciones con vuelta, Home y End
 * saltan a la primera y a la última, y Tab cierra el menú dejando que el foco siga.
 * Escape y el clic afuera los atiende `useDismiss`.
 */
export function onMenuKeyDown(e: KeyboardEvent<HTMLElement>, close: () => void): void {
  if (e.key === 'Tab') {
    close();
    return;
  }
  const items = menuItems(e.currentTarget);
  if (items.length === 0) {
    return;
  }
  const current = items.indexOf(document.activeElement as HTMLElement);
  const last = items.length - 1;
  let next: number;
  switch (e.key) {
    case 'ArrowDown':
      next = current === -1 || current === last ? 0 : current + 1;
      break;
    case 'ArrowUp':
      next = current <= 0 ? last : current - 1;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = last;
      break;
    default:
      return;
  }
  e.preventDefault();
  items[next]?.focus();
}
